/**
 * How one article's text becomes HTML — the ONE implementation (MAR-957, moved here by MAR-948).
 *
 * The full law page (`pages/app/laws/id/[id].vue`, via `components/laws/SectionCard.vue`) and the
 * chat's article panel (`components/LawArticleView.vue`) each carried a `renderSectionHtml`, and they
 * had drifted: the law page's stripped PDF artifacts and rendered the structural markers as headings,
 * while the chat copy did neither. Both import this now; do not grow a third copy for a new surface.
 *
 * The input is corpus text with `**bold**` markers. Everything is HTML-escaped BEFORE any markup is
 * introduced, so the result is safe for `v-html` whatever the text contains.
 */
import { runningHeaders } from './pdfArtifacts'

/**
 * Strip PDF artifacts and trailing structural headings from an article's text. The running header
 * stripped is the norm's own jurisdiction's (`pdfArtifacts.ts`); page numbers and underscore rules go
 * everywhere.
 */
export function cleanSectionText(text: string, jurisdiction: string | null | undefined): string {
  let cleaned = text
  for (const phrase of runningHeaders(jurisdiction).phrases) {
    cleaned = cleaned.replace(new RegExp(`\\n*\\**\\s*(?:${phrase.source})\\s*\\**\\n*`, 'gi'), '\n\n')
  }
  cleaned = cleaned
    .replace(/\n*__{5,}\s*\d*\s*\n*/g, '\n\n')
    .replace(/\n+\s*\d{1,4}\s*\n+/g, '\n\n')

  cleaned = cleaned.replace(/(\n\s*\*\*(?:LIBRO|TÍTUL|TITULO|CAPÍTUL|CAPITULO|SECCIÓN|SECCION)[^*]*\*\*\s*)+$/gi, '')

  return cleaned.trim()
}

/**
 * Markdown list lines (MAR-1434). `- item` is a bullet; `a. item`, `iv. item`, `4. item` are ordered
 * items whose printed marker is the source's own text — legal references cite "inciso a)", so the
 * marker stays in the item and the browser never numbers it (`list-none`). The marker must be followed
 * by whitespace, so `Art. 30.-`, `12.000` or `1.-` never qualify.
 */
const BULLET_LINE = /^-\s+(.*)$/
const ORDERED_LINE = /^(?:\d{1,3}|[a-zA-Z]|[ivxIVX]{1,6})\.\s+\S/

type ListKind = 'ul' | 'ol'

function listKind(line: string): ListKind | null {
  if (BULLET_LINE.test(line)) return 'ul'
  if (ORDERED_LINE.test(line)) return 'ol'
  return null
}

function renderList(kind: ListKind, lines: string[]): string {
  if (kind === 'ul') {
    const items = lines.map((l) => `<li>${l.replace(BULLET_LINE, '$1')}</li>`).join('')
    return `<ul class="mb-3 list-disc pl-5 space-y-1">${items}</ul>`
  }
  const items = lines.map((l) => `<li>${l}</li>`).join('')
  return `<ol class="mb-3 list-none pl-0 space-y-1">${items}</ol>`
}

/**
 * One blank-line-separated block (already escaped). A RUN of two or more list lines of one kind
 * becomes a list; a lone marker line — `1. mes` continuing a sentence — stays text, so a paragraph
 * is only turned into a list when it really is one. Everything else is a `<p>`, as before.
 */
function renderBlock(block: string): string {
  const lines = block.split('\n')
  const out: string[] = []
  let text: string[] = []
  const flushText = () => {
    if (text.length) out.push(`<p class="mb-3">${text.join('\n').trim()}</p>`)
    text = []
  }

  let i = 0
  while (i < lines.length) {
    const kind = listKind(lines[i]!.trim())
    let j = i
    while (kind && j < lines.length && listKind(lines[j]!.trim()) === kind) j++
    if (kind && j - i >= 2) {
      flushText()
      out.push(renderList(kind, lines.slice(i, j).map((l) => l.trim())))
      i = j
    } else {
      text.push(lines[i]!)
      i++
    }
  }
  flushText()
  return out.join('\n')
}

/** Render an article: escape, turn `**bold**` into `<strong>`, style structural headings, render list runs. */
export function renderSectionHtml(text: string, jurisdiction: string | null | undefined): string {
  const cleaned = cleanSectionText(text, jurisdiction)

  let html = cleaned
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  html = html.replace(/\*\*((?:LIBRO|TÍTUL|TITULO)[^*]*)\*\*/gi,
    '<h3 class="text-sm font-semibold text-foreground mt-6 mb-1">$1</h3>')
  html = html.replace(/\*\*((?:CAPÍTUL|CAPITULO)[^*]*)\*\*/gi,
    '<h4 class="text-[13px] font-semibold text-foreground mt-5 mb-1">$1</h4>')
  html = html.replace(/\*\*((?:SECCIÓN|SECCION)[^*]*)\*\*/gi,
    '<h5 class="text-xs font-semibold text-foreground/80 mt-4 mb-1 uppercase tracking-wide">$1</h5>')
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

  html = html
    .split(/\n{2,}/)
    .map((p) => {
      const trimmed = p.trim()
      if (!trimmed) return ''
      if (trimmed.startsWith('<h')) return trimmed
      return renderBlock(trimmed)
    })
    .filter(Boolean)
    .join('\n')

  html = html.replace(/(?<!>)\n(?!<)/g, '<br>')

  return html
}
